import { useEffect, useState } from "react";
import MainLayout from "../../components/layouts/MainLayout";
import { Link, useNavigate, useParams } from "react-router-dom";
import { message, Spin, Alert } from "antd";
import Swal from "sweetalert2";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { LeftCircleOutlined } from "@ant-design/icons";
import { Auth } from "../../utils/Auth";
import { ConditionGeneraleApi } from "../../api/conditionGeneraleApi";
import { typeUsers, documentTypes } from "./constants";


const UpdateConditionGeneraleView = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const abortController = new AbortController();

    const [formData, setFormData] = useState({
        type: 'CGV',
        title: '',
        type_user:'ALL',
        content: '',
    });
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(false);
    const [error, setError] = useState(null);

    const fetchData = async () => {
        setFetching(true);
        setError(null);
        try {
            const res = await ConditionGeneraleApi.getDocumentDetails(id, abortController.signal);
            setFormData({
                type: res.data.type,
                title: res.data.title,
                type_user:res.data.type_user,
                content: res.data.content ?? '',
            });
        } catch (err) {
            setError("Impossible de charger le document.");
        } finally {
            setFetching(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleContentChange = (value) => {
        setFormData({ ...formData, content: value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const res = await ConditionGeneraleApi.updateDocument(id, JSON.stringify(formData), abortController.signal);

            if (!res.success) {
                setLoading(false);
                return Swal.fire({
                    title: "Impossible d'enregistrer",
                    text: res.message,
                });
            }

            message.success('Document juridique mis à jour avec succès');
            navigate(`/conditions-generales/${id}/details`);
        } catch (error) {
            setLoading(false);
            message.error("Une erreur est survenue lors de la mise à jour du document");
            const messages = await error?.messages ?? error?.message;

            if (messages instanceof Array)
                return Swal.fire({
                    title: messages.join('\n') !== 'Unauthenticated' ? "Erreur détectée" : "Session expirée",
                    text: messages.join('\n') !== 'Unauthenticated' ? "Veuillez vous reconncter." : "Votre session a expirée veuillez vous reconnecter",
                    icon: "error",
                }).then(() => {
                    if (messages.join('\n') === 'Unauthenticated') {
                        Auth.removeSessionToken();
                        return navigate(`/login`);
                    }
                });
        } finally {
            setLoading(false);
        }
    };

    return <MainLayout>
        <div className="container mx-auto p-6">
            <div className="flex justify-between">
                <h1 className="text-3xl font-semibold mb-6">Modifier le document juridique</h1>
                <Link to={`/conditions-generales`}><LeftCircleOutlined /> Retour  </Link>
            </div>

            {fetching && <Spin size="large" />}
            {error && <Alert message={error} type="error" />}

            {!fetching && !error && (
                <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg shadow-md space-y-4">
                    <div>
                        <label className="block text-sm font-medium mb-1">Type de document</label>
                        <select
                            name="type"
                            value={formData.type}
                            onChange={handleChange}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md bg-gray-100"
                            disabled
                        >
                            {documentTypes.map((docType) => (
                                <option key={docType.value} value={docType.value}>{docType.label}</option>
                            ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">Le type ne peut pas être modifié après la création (il détermine la numérotation des versions).</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Public cible</label>
                        <select
                            name="type_user"
                            value={formData.type_user}
                            onChange={handleChange}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md"
                            required
                        >
                            {typeUsers.map((typeUser) => (
                                <option key={typeUser.value} value={typeUser.value}>{typeUser.label}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-1">Titre</label>
                        <input
                            type="text"
                            name="title"
                            value={formData.title}
                            onChange={handleChange}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md"
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-1">Contenu</label>
                        <ReactQuill theme="snow" value={formData.content} onChange={handleContentChange} className="bg-white" />
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className="mt-4 px-4 py-2 text-white bg-africasa-secondary rounded hover:bg-africasa-primary disabled:opacity-50"
                    >
                        {loading ? 'Enregistrement...' : 'Enregistrer'}
                    </button>
                </form>
            )}
        </div>
    </MainLayout>
}

export default UpdateConditionGeneraleView
